/**
 * InsightPro™ — Admin: Client Detail  v1.0
 * Route: /admin/clients/:companyId  (SuperAdmin only)
 *
 * Tabs:
 *   Overview  — company info + PBI config
 *   Users     — list, add, edit, reset password, disable
 *   Groups    — list, edit permissions
 *   Settings  — plan, status, suspend/reactivate
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getFirestore, doc, getDoc, setDoc, collection, getDocs,
  updateDoc, deleteDoc, serverTimestamp,
  query, where, orderBy, limit, onSnapshot,
} from 'firebase/firestore';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { writeBatch } from 'firebase/firestore';
import * as XLSX from 'xlsx';

// ── Excel parser shared with the wizard ──────────────────────
function parseExcel(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        resolve(XLSX.utils.sheet_to_json(ws));
      } catch (err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsBinaryString(file);
  });
}

const db      = getFirestore();
const auth    = getAuth();
const storage = getStorage();
const BASE = process.env.REACT_APP_FUNCTIONS_BASE_URL;

// ── API helpers ───────────────────────────────────────────────
async function adminPost(path, body) {
  // Force refresh ensures superAdmin claim is present in token
  const token = await auth.currentUser?.getIdToken(true);
  const r = await fetch(`${BASE}/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
  return data;
}

// ── Shared styles ─────────────────────────────────────────────
const S = {
  page:    { padding: '28px 32px', animation: 'fadeUp 0.4s ease both', maxWidth: 1100 },
  back:    { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', cursor: 'pointer', marginBottom: 20, display: 'inline-flex', alignItems: 'center', gap: 6, letterSpacing: '0.08em' },
  title:   { fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--page-text)', letterSpacing: '-0.02em' },
  sub:     { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', marginTop: 3, letterSpacing: '0.08em' },
  tabs:    { display: 'flex', gap: 4, borderBottom: '1px solid var(--card-border)', marginBottom: 28, marginTop: 24 },
  tab:     (active) => ({
    fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
    padding: '10px 18px', cursor: 'pointer', background: 'none', border: 'none',
    color: active ? 'var(--gold)' : 'var(--page-muted)',
    borderBottom: active ? '2px solid var(--gold)' : '2px solid transparent',
    marginBottom: -1,
  }),
  card:    { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '20px 24px', marginBottom: 16 },
  cardTitle: { fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 16 },
  row:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 14 },
  label:   { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 5 },
  input:   { background: 'var(--page)', border: '1px solid var(--card-border)', padding: '9px 12px', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--page-text)', outline: 'none', borderRadius: 2, width: '100%' },
  select:  { background: 'var(--page)', border: '1px solid var(--card-border)', padding: '9px 12px', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--page-text)', outline: 'none', borderRadius: 2, width: '100%' },
  saveBtn: { fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'var(--gold)', color: 'var(--void)', border: 'none', padding: '9px 20px', cursor: 'pointer', borderRadius: 2 },
  dangerBtn: { fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'transparent', color: 'var(--red)', border: '1px solid var(--red)', padding: '8px 18px', cursor: 'pointer', borderRadius: 2 },
  ghostBtn: { fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'transparent', color: 'var(--page-muted)', border: '1px solid var(--card-border)', padding: '8px 16px', cursor: 'pointer', borderRadius: 2 },
  successMsg: { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--green-l)', padding: '8px 12px', background: 'rgba(26,173,99,0.08)', border: '1px solid rgba(26,173,99,0.2)', borderRadius: 2 },
  errorMsg:   { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--red)', padding: '8px 12px', background: 'rgba(176,40,40,0.08)', border: '1px solid rgba(176,40,40,0.2)', borderRadius: 2 },
  table:   { width: '100%', borderCollapse: 'collapse' },
  th:      { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--page-muted)', padding: '10px 14px', textAlign: 'left', borderBottom: '1px solid var(--card-border)', background: 'var(--page)' },
  td:      { padding: '12px 14px', fontSize: 13, color: 'var(--page-text)', borderBottom: '1px solid var(--card-border)', verticalAlign: 'middle' },
  tdM:     { padding: '12px 14px', fontSize: 11, color: 'var(--page-muted)', borderBottom: '1px solid var(--card-border)', fontFamily: 'var(--font-mono)', verticalAlign: 'middle' },
  actionBtn: { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.08em', padding: '4px 10px', cursor: 'pointer', background: 'transparent', border: '1px solid var(--card-border)', color: 'var(--page-muted)', borderRadius: 2, marginRight: 6 },
};

// ── Field component ───────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div>
      <div style={S.label}>{label}</div>
      {children}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: OVERVIEW
// ════════════════════════════════════════════════════════════
function TabOverview({ company, companyId, onRefresh }) {
  const [pbi,          setPbi]          = useState({ workspaceId: '', datasetId: '', serviceAccount: '' });
  const [details,      setDetails]      = useState({ name: '', tradingName: '', billingEmail: '', status: 'active' });
  const [saving,       setSaving]       = useState(false);
  const [savingDet,    setSavingDet]    = useState(false);
  const [msg,          setMsg]          = useState(null);
  const [msgDet,       setMsgDet]       = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoProgress,  setLogoProgress]  = useState(0);
  const [logoPreview,   setLogoPreview]   = useState(null);
  const logoInputRef = React.useRef(null);

  useEffect(() => {
    if (company?.pbi) {
      setPbi({
        workspaceId:    company.pbi.workspaceId    || '',
        datasetId:      company.pbi.datasetId      || '',
        serviceAccount: company.pbi.serviceAccount || `${company.slug}@insightpro.co.za`,
      });
    }
    if (company) {
      setDetails({
        name:         company.name         || '',
        tradingName:  company.tradingName  || '',
        billingEmail: company.billingEmail || '',
        status:       company.status       || 'active',
      });
      setLogoPreview(company.logoUrl || null);
    }
  }, [company]);

  const savePbi = async () => {
    setSaving(true); setMsg(null);
    try {
      await updateDoc(doc(db, 'companies', companyId), {
        pbi: { ...company.pbi, ...pbi, configuredAt: serverTimestamp() },
        updatedAt: serverTimestamp(),
      });
      setMsg({ type: 'success', text: 'Power BI config saved.' });
      onRefresh();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setSaving(false); }
  };

  const saveDetails = async () => {
    setSavingDet(true); setMsgDet(null);
    try {
      await updateDoc(doc(db, 'companies', companyId), {
        name:         details.name,
        tradingName:  details.tradingName,
        billingEmail: details.billingEmail,
        status:       details.status,
        updatedAt:    serverTimestamp(),
      });
      // Sync companyName to all userMemberships via server
      const usersSnap = await getDocs(collection(db, 'companies', companyId, 'users'));
      for (const userDoc of usersSnap.docs) {
        const email = userDoc.data().email;
        if (!email) continue;
        try {
          await adminPost('syncUserMembership', { companyId, email, fields: { companyName: details.name } });
        } catch {}
      }
      setMsgDet({ type: 'success', text: 'Company details saved and synced.' });
      onRefresh();
    } catch (e) { setMsgDet({ type: 'error', text: e.message }); }
    finally { setSavingDet(false); }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setMsgDet({ type: 'error', text: 'Max 2MB' }); return; }
    if (!file.type.startsWith('image/')) { setMsgDet({ type: 'error', text: 'Must be an image' }); return; }
    setLogoUploading(true); setLogoProgress(0);
    try {
      const ext  = file.name.split('.').pop();
      const sRef = storageRef(storage, `logos/${company.slug}_${Date.now()}.${ext}`);
      const task = uploadBytesResumable(sRef, file);
      task.on('state_changed',
        snap => setLogoProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
        err  => { setMsgDet({ type: 'error', text: err.message }); setLogoUploading(false); },
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          // Save to company doc
          await updateDoc(doc(db, 'companies', companyId), { logoUrl: url, updatedAt: serverTimestamp() });
          // Sync logoUrl to all userMemberships using email→UID lookup
          const usersSnap2 = await getDocs(collection(db, 'companies', companyId, 'users'));
          for (const userDoc of usersSnap2.docs) {
            const email = userDoc.data().email;
            if (!email) continue;
            try {
              // Use adminPost to sync — server has admin SDK access to get UID by email
              await adminPost('syncUserMembership', { companyId, email, fields: { logoUrl: url } });
            } catch {}
          }
          setLogoPreview(url);
          setLogoUploading(false);
          setMsgDet({ type: 'success', text: 'Logo uploaded and synced to all users.' });
          onRefresh();
        }
      );
    } catch (e) { setMsgDet({ type: 'error', text: e.message }); setLogoUploading(false); }
  };

  return (
    <div>
      {/* Company Info — editable */}
      <div style={S.card}>
        <div style={S.cardTitle}>Company Information</div>

        {/* Logo upload */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid var(--card-border)' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 8, flexShrink: 0,
            background: logoPreview ? '#fff' : 'var(--page)',
            border: '1px solid var(--card-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', cursor: 'pointer',
          }} onClick={() => logoInputRef.current?.click()}>
            {logoPreview
              ? <img src={logoPreview} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }} />
              : <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)' }}>LOGO</span>
            }
          </div>
          <div>
            <button style={{ ...S.ghostBtn, fontSize: 11 }} onClick={() => logoInputRef.current?.click()} disabled={logoUploading}>
              {logoUploading ? `Uploading ${logoProgress}%...` : logoPreview ? 'Change Logo' : 'Upload Logo'}
            </button>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)', marginTop: 4 }}>
              PNG, JPG or SVG · Max 2MB · Syncs to all user sidebars
            </div>
          </div>
          <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => handleLogoUpload(e.target.files[0])} />
        </div>

        <div style={S.row}>
          <Field label="Company Name *">
            <input style={S.input} value={details.name} onChange={e => setDetails(p => ({ ...p, name: e.target.value }))} />
          </Field>
          <Field label="Trading Name">
            <input style={S.input} value={details.tradingName} onChange={e => setDetails(p => ({ ...p, tradingName: e.target.value }))} />
          </Field>
        </div>
        <div style={S.row}>
          <Field label="Billing Email">
            <input style={S.input} type="email" value={details.billingEmail} onChange={e => setDetails(p => ({ ...p, billingEmail: e.target.value }))} />
          </Field>
          <Field label="Status">
            <select style={S.select} value={details.status} onChange={e => setDetails(p => ({ ...p, status: e.target.value }))}>
              <option value="active">Active</option>
              <option value="onboarding">Onboarding</option>
              <option value="suspended">Suspended</option>
            </select>
          </Field>
        </div>
        <div style={S.row}>
          <Field label="Slug"><div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--page-muted)' }}>{company?.slug}</div></Field>
          <Field label="Plan"><div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--gold)' }}>{company?.plan}</div></Field>
        </div>
        <div style={S.row}>
          <Field label="BigQuery Dataset"><div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' }}>{company?.bigQueryDataset || '—'}</div></Field>
          <Field label="Industry"><div style={{ fontSize: 13 }}>{company?.industry || '—'}</div></Field>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
          <button style={S.saveBtn} onClick={saveDetails} disabled={savingDet}>{savingDet ? 'Saving...' : 'Save Details'}</button>
          {msgDet && <div style={msgDet.type === 'success' ? S.successMsg : S.errorMsg}>{msgDet.text}</div>}
        </div>
      </div>

      {/* Power BI Config */}
      <div style={S.card}>
        <div style={S.cardTitle}>Power BI Configuration</div>
        <div style={{ fontSize: 12, color: 'var(--page-muted)', marginBottom: 16, lineHeight: 1.6 }}>
          Enter the client's Power BI workspace and dataset IDs. The service account email is the
          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--gold)', marginLeft: 4 }}>@insightpro.co.za</span> account
          with the Pro licence assigned. Credentials (password) are stored separately in Secret Manager via CLI.
        </div>
        <div style={S.row}>
          <Field label="Workspace ID">
            <input style={S.input} value={pbi.workspaceId} onChange={e => setPbi(p => ({ ...p, workspaceId: e.target.value }))} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
          </Field>
          <Field label="Dataset ID">
            <input style={S.input} value={pbi.datasetId} onChange={e => setPbi(p => ({ ...p, datasetId: e.target.value }))} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
          </Field>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Field label="Service Account Email">
            <input style={S.input} value={pbi.serviceAccount} onChange={e => setPbi(p => ({ ...p, serviceAccount: e.target.value }))} placeholder={`${company?.slug}@insightpro.co.za`} />
          </Field>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button style={S.saveBtn} onClick={savePbi} disabled={saving}>{saving ? 'Saving...' : 'Save PBI Config'}</button>
          {msg && <div style={msg.type === 'success' ? S.successMsg : S.errorMsg}>{msg.text}</div>}
        </div>
        <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--page)', border: '1px solid var(--card-border)', borderRadius: 2 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)', marginBottom: 6, letterSpacing: '0.1em' }}>SECRET MANAGER — SET PASSWORD VIA CLI</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--gold)' }}>
            firebase functions:secrets:set insightpro-pbi-{company?.slug}
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', marginTop: 4 }}>
            Enter: {'{'}  "email": "{pbi.serviceAccount || `${company?.slug}@insightpro.co.za`}", "password": "..."  {'}'}
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: USERS
// ════════════════════════════════════════════════════════════
function TabUsers({ companyId, company }) {
  const [users,      setUsers]      = useState([]);
  const [groups,     setGroups]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showAdd,    setShowAdd]    = useState(false);
  const [showAddEx,  setShowAddEx]  = useState(false);
  const [newUser,    setNewUser]    = useState({ firstName: '', lastName: '', email: '', groupId: '', branchId: '' });
  const [exUser,     setExUser]     = useState({ email: '', groupId: '' });
  const [saving,     setSaving]     = useState(false);
  const [msg,        setMsg]        = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [uSnap, gSnap] = await Promise.all([
        getDocs(collection(db, 'companies', companyId, 'users')),
        getDocs(collection(db, 'companies', companyId, 'userGroups')),
      ]);
      setUsers(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setGroups(gSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const inviteUser = async () => {
    if (!newUser.firstName || !newUser.email) {
      setMsg({ type: 'error', text: 'First name and email are required.' }); return;
    }
    setSaving(true); setMsg(null);
    try {
      await adminPost('inviteUser', { companyId, ...newUser, sendInvite: true });
      setMsg({ type: 'success', text: `Invite sent to ${newUser.email}` });
      setNewUser({ firstName: '', lastName: '', email: '', groupId: '', branchId: '' });
      setShowAdd(false);
      load();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setSaving(false); }
  };

  const addExistingUser = async () => {
    if (!exUser.email) { setMsg({ type: 'error', text: 'Email is required.' }); return; }
    setSaving(true); setMsg(null);
    try {
      // Look up user across all companies by email
      const db2 = getFirestore();
      const { collection: col2, query: q2, where: w2, getDocs: gd2, setDoc: sd2, doc: d2, serverTimestamp } = await import('firebase/firestore');
      
      // Find user in any company's users subcollection
      let foundUid = null, foundData = null;
      const companiesSnap = await gd2(col2(db2, 'companies'));
      for (const cDoc of companiesSnap.docs) {
        const usersSnap = await gd2(q2(col2(db2, 'companies', cDoc.id, 'users'), w2('email', '==', exUser.email)));
        if (!usersSnap.empty) {
          foundUid  = usersSnap.docs[0].id;
          foundData = usersSnap.docs[0].data();
          break;
        }
      }

      if (!foundUid) {
        setMsg({ type: 'error', text: `No user found with email ${exUser.email}. They must have an existing account.` });
        setSaving(false); return;
      }

      // Get group permissions
      const groupDoc = exUser.groupId
        ? await (await import('firebase/firestore')).getDoc(d2(db2, 'companies', companyId, 'userGroups', exUser.groupId))
        : null;
      const permissions = groupDoc?.exists() ? groupDoc.data().permissions || {} : {};
      const branchScope = groupDoc?.exists() ? groupDoc.data().branchScope || 'all' : 'all';

      // Write to companies/{id}/users
      await sd2(d2(db2, 'companies', companyId, 'users', foundUid), {
        uid:         foundUid,
        firstName:   foundData.firstName || '',
        lastName:    foundData.lastName  || '',
        email:       exUser.email,
        phone:       foundData.phone     || '',
        groupId:     exUser.groupId      || null,
        branchId:    null,
        role:        foundData.role      || 'user',
        status:      'active',
        createdAt:   serverTimestamp(),
        lastLogin:   null,
      }, { merge: true });

      // Write to userMemberships
      await sd2(d2(db2, 'userMemberships', foundUid, 'companies', companyId), {
        companyId,
        companyName: company?.name || company?.tradingName || '',
        logoUrl:     company?.logoUrl || null,
        slug:        company?.slug   || '',
        role:        foundData.role  || 'user',
        groupId:     exUser.groupId  || null,
        branchId:    null,
        branchScope,
        permissions,
        status:      'active',
        joinedAt:    serverTimestamp(),
      });

      setMsg({ type: 'success', text: `${exUser.email} added to this company successfully.` });
      setExUser({ email: '', groupId: '' });
      setShowAddEx(false);
      load();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setSaving(false); }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      setMsg({ type: 'success', text: `Password reset email sent to ${email}` });
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
  };

  const toggleUser = async (uid, disabled) => {
    try {
      await adminPost('updateUserClaims', { companyId, uid, status: disabled ? 'active' : 'suspended' });
      setMsg({ type: 'success', text: `User ${disabled ? 'reactivated' : 'suspended'}.` });
      load();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
  };

  const deleteUser = async (uid, email) => {
    if (!window.confirm(`Permanently delete ${email}? This cannot be undone.`)) return;
    setSaving(true); setMsg(null);
    try {
      await adminPost('deleteUser', { companyId, uid });
      setMsg({ type: 'success', text: `${email} deleted successfully.` });
      load();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setSaving(false); }
  };

  const changeGroup = async (uid, groupId) => {
    try {
      await adminPost('updateUserClaims', { companyId, uid, groupId });
      setMsg({ type: 'success', text: 'User group updated.' });
      load();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', letterSpacing: '0.1em' }}>
          {users.length} user{users.length !== 1 ? 's' : ''}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...S.ghostBtn, fontSize: 11 }} onClick={() => { setShowAddEx(o => !o); setShowAdd(false); }}>
            {showAddEx ? '— Cancel' : '+ Existing User'}
          </button>
          <button style={S.saveBtn} onClick={() => { setShowAdd(o => !o); setShowAddEx(false); }}>
            {showAdd ? '— Cancel' : '+ Invite New'}
          </button>
        </div>
      </div>

      {msg && <div style={{ ...( msg.type === 'success' ? S.successMsg : S.errorMsg), marginBottom: 16 }}>{msg.text}</div>}

      {/* Add existing user form */}
      {showAddEx && (
        <div style={{ ...S.card, background: 'var(--page)', marginBottom: 20 }}>
          <div style={S.cardTitle}>Add Existing User</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', marginBottom: 14, letterSpacing: '0.05em' }}>
            Grants an existing InsightPro user access to this company. No invite email is sent.
          </div>
          <div style={S.row}>
            <Field label="Email Address *">
              <input style={S.input} type="email" value={exUser.email}
                onChange={e => setExUser(p => ({ ...p, email: e.target.value }))}
                placeholder="existing@user.co.za" />
            </Field>
            <Field label="User Group">
              <select style={S.select} value={exUser.groupId}
                onChange={e => setExUser(p => ({ ...p, groupId: e.target.value }))}>
                <option value="">Select group...</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={S.saveBtn} onClick={addExistingUser} disabled={saving}>
              {saving ? 'Adding...' : 'Add to Company'}
            </button>
            <button style={S.ghostBtn} onClick={() => setShowAddEx(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Add user form */}
      {showAdd && (
        <div style={{ ...S.card, background: 'var(--page)', marginBottom: 20 }}>
          <div style={S.cardTitle}>Invite New User</div>
          <div style={S.row}>
            <Field label="First Name *">
              <input style={S.input} value={newUser.firstName} onChange={e => setNewUser(p => ({ ...p, firstName: e.target.value }))} placeholder="First Name" />
            </Field>
            <Field label="Last Name">
              <input style={S.input} value={newUser.lastName} onChange={e => setNewUser(p => ({ ...p, lastName: e.target.value }))} placeholder="Last Name" />
            </Field>
          </div>
          <div style={{ ...S.row, marginBottom: 14 }}>
            <Field label="Email Address *">
              <input style={S.input} type="email" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} placeholder="user@company.co.za" />
            </Field>
            <Field label="User Group">
              <select style={S.select} value={newUser.groupId} onChange={e => setNewUser(p => ({ ...p, groupId: e.target.value }))}>
                <option value="">Select group...</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={S.saveBtn} onClick={inviteUser} disabled={saving}>{saving ? 'Sending...' : 'Send Invite'}</button>
            <button style={S.ghostBtn} onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* User table */}
      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' }}>Loading users...</div>
      ) : (
        <div style={{ background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, overflow: 'hidden' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {['Name', 'Email', 'Role', 'Group', 'Status', 'Last Login', 'Actions'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: 'var(--page-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>No users yet</td></tr>
              ) : users.map(u => (
                <tr key={u.id}>
                  <td style={S.td}><div style={{ fontWeight: 500 }}>{u.firstName} {u.lastName}</div></td>
                  <td style={S.tdM}>{u.email}</td>
                  <td style={S.tdM}>{u.role}</td>
                  <td style={S.td}>
                    <select
                      style={{ ...S.select, width: 'auto', padding: '4px 8px', fontSize: 11 }}
                      value={u.groupId || ''}
                      onChange={e => changeGroup(u.id, e.target.value)}
                    >
                      <option value="">No group</option>
                      {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </td>
                  <td style={S.td}>
                    <span style={{
                      fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.1em',
                      padding: '3px 7px', borderRadius: 2,
                      background: u.status === 'active' ? 'rgba(26,173,99,0.1)' : u.status === 'invited' ? 'rgba(196,122,26,0.1)' : 'rgba(176,40,40,0.1)',
                      color: u.status === 'active' ? 'var(--green-l)' : u.status === 'invited' ? 'var(--amber)' : 'var(--red)',
                    }}>{u.status || 'invited'}</span>
                  </td>
                  <td style={S.tdM}>{u.lastLogin?.toDate?.()?.toLocaleDateString('en-ZA') || 'Never'}</td>
                  <td style={S.td}>
                    <button style={S.actionBtn} onClick={() => resetPassword(u.email)} title="Send password reset">Reset PW</button>
                    <button
                      style={{ ...S.actionBtn, color: u.status === 'suspended' ? 'var(--green-l)' : 'var(--red)', borderColor: u.status === 'suspended' ? 'rgba(26,173,99,0.3)' : 'rgba(176,40,40,0.3)' }}
                      onClick={() => toggleUser(u.id, u.status === 'suspended')}
                    >{u.status === 'suspended' ? 'Reactivate' : 'Suspend'}</button>
                    <button
                      style={{ ...S.actionBtn, color: 'var(--red)', borderColor: 'rgba(176,40,40,0.3)', marginLeft: 4 }}
                      onClick={() => deleteUser(u.id, u.email)}
                    >Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: GROUPS
// ════════════════════════════════════════════════════════════
function TabGroups({ companyId, company }) {
  const [groups,  setGroups]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null);

  // Solution-scoped permission list — keep retail tenants from seeing finance
  // perms and vice versa. Mirrors the keys seeded by OnboardWizard.
  const PERMISSIONS_RETAIL = [
    'executiveOverview', 'sales', 'margin', 'stock',
    'debtors', 'branches', 'advisory', 'actions', 'reports',
  ];
  const PERMISSIONS_FINANCE = [
    'groupPL', 'balanceSheet', 'expenseAnalysis', 'cashFlow',
    'storePerformance', 'dcPerformance', 'variance', 'groupConsol', 'notes',
  ];
  const PERMISSIONS = company?.solution === 'MultiEntityFinance'
    ? PERMISSIONS_FINANCE
    : PERMISSIONS_RETAIL;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'companies', companyId, 'userGroups'));
      setGroups(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const saveGroup = async () => {
    if (!editing) return;
    setSaving(true); setMsg(null);
    try {
      await updateDoc(doc(db, 'companies', companyId, 'userGroups', editing.id), {
        name:        editing.name,
        description: editing.description,
        branchScope: editing.branchScope,
        permissions: editing.permissions,
        updatedAt:   serverTimestamp(),
      });

      // Fan-out: members snapshot permissions at invite time (adminFunctions.js
      // line 509), so editing the group alone leaves them on stale perms. Push
      // the new perms onto every active member's userMemberships doc here.
      const usersSnap = await getDocs(query(
        collection(db, 'companies', companyId, 'users'),
        where('groupId', '==', editing.id),
      ));
      let updated = 0;
      if (!usersSnap.empty) {
        const batch = writeBatch(db);
        usersSnap.forEach(u => {
          batch.set(
            doc(db, 'userMemberships', u.id, 'companies', companyId),
            { permissions: editing.permissions, branchScope: editing.branchScope },
            { merge: true },
          );
          updated += 1;
        });
        await batch.commit();
      }

      setMsg({ type: 'success', text: `Group "${editing.name}" saved · ${updated} member${updated === 1 ? '' : 's'} synced.` });
      setEditing(null);
      load();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setSaving(false); }
  };

  const togglePerm = (key, field) => {
    setEditing(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: { ...prev.permissions[key], [field]: !prev.permissions[key]?.[field] },
      },
    }));
  };

  return (
    <div>
      {msg && <div style={{ ...(msg.type === 'success' ? S.successMsg : S.errorMsg), marginBottom: 16 }}>{msg.text}</div>}

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' }}>Loading groups...</div>
      ) : groups.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' }}>No groups configured</div>
      ) : groups.map(g => (
        <div key={g.id} style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--page-text)' }}>{g.name}</div>
              {g.description && <div style={{ fontSize: 12, color: 'var(--page-muted)', marginTop: 2 }}>{g.description}</div>}
            </div>
            <button style={S.ghostBtn} onClick={() => setEditing(editing?.id === g.id ? null : JSON.parse(JSON.stringify(g)))}>
              {editing?.id === g.id ? 'Cancel' : 'Edit'}
            </button>
          </div>

          {editing?.id === g.id ? (
            <div>
              <div style={S.row}>
                <Field label="Group Name">
                  <input style={S.input} value={editing.name} onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} />
                </Field>
                <Field label="Branch Scope">
                  <select style={S.select} value={editing.branchScope} onChange={e => setEditing(p => ({ ...p, branchScope: e.target.value }))}>
                    <option value="all">All branches</option>
                    <option value="assigned-hard">Assigned branches only</option>
                    <option value="assigned-soft">Assigned branches (default)</option>
                  </select>
                </Field>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Field label="Description">
                  <input style={S.input} value={editing.description || ''} onChange={e => setEditing(p => ({ ...p, description: e.target.value }))} />
                </Field>
              </div>
              {/* Permissions grid */}
              <div style={S.cardTitle}>Permissions</div>
              <div style={{ background: 'var(--page)', border: '1px solid var(--card-border)', borderRadius: 2, overflow: 'hidden', marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={S.th}>Module</th>
                      <th style={{ ...S.th, textAlign: 'center', width: 80 }}>View</th>
                      <th style={{ ...S.th, textAlign: 'center', width: 80 }}>Export</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PERMISSIONS.map(key => (
                      <tr key={key}>
                        <td style={S.td} style={{ padding: '8px 14px', fontSize: 12, color: 'var(--page-text)', borderBottom: '1px solid var(--card-border)' }}>
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                        </td>
                        <td style={{ textAlign: 'center', borderBottom: '1px solid var(--card-border)', padding: '8px' }}>
                          <input type="checkbox" checked={!!editing.permissions?.[key]?.view} onChange={() => togglePerm(key, 'view')} />
                        </td>
                        <td style={{ textAlign: 'center', borderBottom: '1px solid var(--card-border)', padding: '8px' }}>
                          <input type="checkbox" checked={!!editing.permissions?.[key]?.export} onChange={() => togglePerm(key, 'export')} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button style={S.saveBtn} onClick={saveGroup} disabled={saving}>{saving ? 'Saving...' : 'Save Group'}</button>
                <button style={S.ghostBtn} onClick={() => setEditing(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div>
                <div style={S.label}>Branch Scope</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' }}>{g.branchScope || 'all'}</div>
              </div>
              <div>
                <div style={S.label}>Permissions</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' }}>
                  {Object.entries(g.permissions || {}).filter(([, v]) => v?.view).map(([k]) => k).join(', ') || 'None'}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: SETTINGS
// ════════════════════════════════════════════════════════════
function TabAudit({ companyId }) {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { query, orderBy, limit, getDocs, collection: col } = await import('firebase/firestore');
        const snap = await getDocs(
          query(col(db, 'companies', companyId, 'auditLog'),
            orderBy('timestamp', 'desc'), limit(100))
        );
        setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [companyId]);

  const ACTION_COLORS = {
    USER_LOGIN:          'var(--green-l)',
    USER_INVITED:        'var(--gold)',
    USER_DELETED:        'var(--red)',
    USER_CLAIMS_UPDATED: 'var(--amber)',
    COMPANY_PROVISIONED: 'var(--gold)',
    COMPANY_SUSPENDED:   'var(--red)',
  };

  return (
    <div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', marginBottom: 16, letterSpacing: '0.1em' }}>
        AUDIT LOG — last 100 events
      </div>
      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--page-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>Loading...</div>
      ) : logs.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--page-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>No audit events yet</div>
      ) : (
        <div style={{ background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, overflow: 'hidden' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {['Timestamp', 'Action', 'User', 'Detail'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id}>
                  <td style={S.tdM}>
                    {log.timestamp?.toDate?.()?.toLocaleString('en-ZA', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    }) || '—'}
                  </td>
                  <td style={S.td}>
                    <span style={{
                      fontFamily: 'var(--font-mono)', fontSize: 9,
                      letterSpacing: '0.08em', padding: '2px 6px', borderRadius: 2,
                      background: `${ACTION_COLORS[log.action] || 'var(--page-muted)'}18`,
                      color: ACTION_COLORS[log.action] || 'var(--page-muted)',
                    }}>{log.action}</span>
                  </td>
                  <td style={S.tdM}>{log.userId?.slice(0, 12) || '—'}</td>
                  <td style={S.tdM}>{log.detail || log.entityId?.slice(0, 16) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TabSettings({ company, companyId, onRefresh, navigate }) {
  const [form,   setForm]   = useState({});
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState(null);
  const [procure, setProcure] = useState({ clerkEmail: '' });
  const [procSaving, setProcSaving] = useState(false);

  useEffect(() => {
    if (company) setForm({
      name:         company.name         || '',
      tradingName:  company.tradingName  || '',
      billingEmail: company.billingEmail || '',
      plan:         company.plan         || 'essential',
      status:       company.status       || 'onboarding',
    });
  }, [company]);

  // Load procurement settings from companies/{id}/settings/procurement
  useEffect(() => {
    if (!companyId) return;
    getDoc(doc(db, 'companies', companyId, 'settings', 'procurement'))
      .then(snap => { if (snap.exists()) setProcure(snap.data()); })
      .catch(() => {});
  }, [companyId]);

  const saveProcurement = async () => {
    setProcSaving(true); setMsg(null);
    try {
      await setDoc(doc(db, 'companies', companyId, 'settings', 'procurement'), {
        ...procure,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setMsg({ type: 'success', text: 'Procurement settings saved.' });
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setProcSaving(false); }
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await updateDoc(doc(db, 'companies', companyId), {
        name:         form.name,
        tradingName:  form.tradingName,
        billingEmail: form.billingEmail,
        plan:         form.plan,
        updatedAt:    serverTimestamp(),
      });
      setMsg({ type: 'success', text: 'Settings saved.' });
      onRefresh();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setSaving(false); }
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm,  setDeleteConfirm]  = useState('');
  const [deleteError,    setDeleteError]    = useState('');

  const deleteCompany = async () => {
    if (deleteConfirm !== company?.name) return;
    setSaving(true); setDeleteError('');
    try {
      await deleteDoc(doc(db, 'companies', companyId));
      navigate('/admin/clients');
    } catch (e) {
      setDeleteError(e.message);
      setSaving(false);
    }
  };

  const suspend = async () => {
    if (!window.confirm(`Suspend ${company?.name}? All users will be disabled.`)) return;
    setSaving(true);
    try {
      await adminPost('suspendCompany', { companyId });
      setMsg({ type: 'success', text: 'Company suspended.' });
      onRefresh();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setSaving(false); }
  };

  const reactivate = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'companies', companyId), { status: 'active', updatedAt: serverTimestamp() });
      setMsg({ type: 'success', text: 'Company reactivated.' });
      onRefresh();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setSaving(false); }
  };

  return (
    <div>
      {msg && <div style={{ ...(msg.type === 'success' ? S.successMsg : S.errorMsg), marginBottom: 16 }}>{msg.text}</div>}

      <div style={S.card}>
        <div style={S.cardTitle}>Company Details</div>
        <div style={S.row}>
          <Field label="Company Name">
            <input style={S.input} value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </Field>
          <Field label="Trading Name">
            <input style={S.input} value={form.tradingName || ''} onChange={e => setForm(p => ({ ...p, tradingName: e.target.value }))} />
          </Field>
        </div>
        <div style={{ ...S.row, marginBottom: 14 }}>
          <Field label="Billing Email">
            <input style={S.input} value={form.billingEmail || ''} onChange={e => setForm(p => ({ ...p, billingEmail: e.target.value }))} />
          </Field>
          <Field label="Plan">
            <select style={S.select} value={form.plan || 'essential'} onChange={e => setForm(p => ({ ...p, plan: e.target.value }))}>
              <option value="essential">Essential</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={S.saveBtn} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>

      {/* Procurement */}
      <div style={S.card}>
        <div style={S.cardTitle}>Procurement</div>
        <div style={S.row}>
          <Field label="Order Clerk Email">
            <input style={S.input} type="email" value={procure.clerkEmail || ''}
              placeholder="clerk@company.co.za"
              onChange={e => setProcure(p => ({ ...p, clerkEmail: e.target.value }))} />
          </Field>
          <Field label="Supplier Contact Email (optional)">
            <input style={S.input} type="email" value={procure.supplierEmail || ''}
              placeholder="orders@supplier.co.za"
              onChange={e => setProcure(p => ({ ...p, supplierEmail: e.target.value }))} />
          </Field>
        </div>
        <div style={{ fontSize: 11, color: 'var(--page-muted)', marginBottom: 14 }}>
          The clerk email receives order notifications from the Order Builder.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={S.saveBtn} onClick={saveProcurement} disabled={procSaving}>
            {procSaving ? 'Saving...' : 'Save Procurement'}
          </button>
        </div>
      </div>

      {/* Danger zone */}
      <div style={{ ...S.card, borderColor: 'rgba(176,40,40,0.3)' }}>
        <div style={{ ...S.cardTitle, color: 'var(--red)' }}>Danger Zone</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Suspend / Reactivate */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            {company?.status !== 'suspended' ? (
              <>
                <button style={S.dangerBtn} onClick={suspend} disabled={saving}>Suspend Company</button>
                <div style={{ fontSize: 12, color: 'var(--page-muted)' }}>Disables all users and marks the company as suspended.</div>
              </>
            ) : (
              <>
                <button style={{ ...S.saveBtn, background: 'var(--green-l)' }} onClick={reactivate} disabled={saving}>Reactivate Company</button>
                <div style={{ fontSize: 12, color: 'var(--page-muted)' }}>Restores company status to active. Users must be re-enabled individually.</div>
              </>
            )}
          </div>
          {/* Delete */}
          <div style={{ borderTop: '1px solid rgba(176,40,40,0.2)', paddingTop: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button style={{ ...S.dangerBtn, borderColor: 'var(--red)', color: 'var(--red)' }}
              onClick={() => setShowDeleteModal(true)} disabled={saving}>
              Delete Company
            </button>
            <div style={{ fontSize: 12, color: 'var(--page-muted)' }}>
              Permanently removes the company record from Firestore. This cannot be undone.
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: 'var(--card)', border: '1px solid rgba(176,40,40,0.4)', borderRadius: 6, padding: 28, maxWidth: 440, width: '100%' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: 'var(--red)', marginBottom: 8 }}>Delete Company</div>
            <div style={{ fontSize: 13, color: 'var(--page-muted)', lineHeight: 1.6, marginBottom: 20 }}>
              This will permanently delete <strong style={{ color: 'var(--page-text)' }}>{company?.name}</strong> from Firestore.
              Users in Firebase Auth will not be deleted — do that separately if needed.
              <br/><br/>
              Type <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--red)' }}>{company?.name}</strong> to confirm.
            </div>
            <input
              style={{ ...S.input, borderColor: 'rgba(176,40,40,0.4)', marginBottom: 16 }}
              placeholder={`Type "${company?.name}" to confirm`}
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
            />
            {deleteError && <div style={{ ...S.errorMsg, marginBottom: 12 }}>{deleteError}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={S.ghostBtn} onClick={() => { setShowDeleteModal(false); setDeleteConfirm(''); setDeleteError(''); }}>Cancel</button>
              <button
                style={{ ...S.dangerBtn, background: deleteConfirm === company?.name ? 'var(--red)' : 'transparent', color: deleteConfirm === company?.name ? '#fff' : 'var(--red)', transition: 'all 0.15s' }}
                disabled={deleteConfirm !== company?.name || saving}
                onClick={deleteCompany}
              >
                {saving ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════
// TAB: MONTHLY PACKS  (Multi-Entity Finance only)
// ════════════════════════════════════════════════════════════
function TabMonthlyPacks({ companyId, company }) {
  const slug = company.slug || '';
  const today = new Date();
  const defaultPeriod = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

  const [periodKey, setPeriodKey] = useState(defaultPeriod);
  const [file, setFile]           = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadErr, setUploadErr] = useState(null);
  const [runs, setRuns]           = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [expandedRun, setExpandedRun] = useState(null); // runId of row showing coverage detail
  const [approving, setApproving]     = useState(null); // runId currently being approved
  const [waitingForRun, setWaitingForRun] = useState(false); // upload done, waiting for parser to create run doc
  const [uploadFinishedAt, setUploadFinishedAt] = useState(null); // ms since epoch — to recognise OUR run
  const [tick, setTick] = useState(0); // drives elapsed-time re-render

  // ── Live history of ingest runs ────────────────────────────
  useEffect(() => {
    const q = query(
      collection(db, 'companies', companyId, 'ingestRuns'),
      orderBy('startedAt', 'desc'),
      limit(20)
    );
    const unsub = onSnapshot(q, snap => {
      setRuns(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setRunsLoading(false);
    }, err => {
      console.error('ingestRuns subscription:', err);
      setRunsLoading(false);
    });
    return () => unsub();
  }, [companyId]);

  // Latest run + helper
  const latestRun = runs[0];
  const runStartMs = (r) => {
    if (!r?.startedAt) return null;
    const d = r.startedAt.toDate?.() || new Date(r.startedAt);
    return d ? d.getTime() : null;
  };

  // When OUR upload's run doc appears in the feed, clear the "waiting" banner.
  // Generous 60s tolerance to absorb any browser-vs-server clock skew, and a
  // hard 60s safety timeout so the banner can't get stuck even if matching
  // misses (e.g. if the run was completed in <1s and we never observe a
  // 'running' state, or if the subscription is slow).
  useEffect(() => {
    if (!waitingForRun || !uploadFinishedAt) return;
    const ourRun = runs.find(r => {
      const t = runStartMs(r);
      return t != null && t >= uploadFinishedAt - 60000;
    });
    if (ourRun) setWaitingForRun(false);
  }, [runs, waitingForRun, uploadFinishedAt]);

  useEffect(() => {
    if (!waitingForRun) return;
    const timeout = setTimeout(() => setWaitingForRun(false), 60000);
    return () => clearTimeout(timeout);
  }, [waitingForRun]);

  // Tick every second while anything is in flight, so elapsed-time updates.
  useEffect(() => {
    const inFlight = waitingForRun || (latestRun && latestRun.status === 'running');
    if (!inFlight) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [waitingForRun, latestRun]);

  // ── Upload handler ─────────────────────────────────────────
  const handleUpload = async () => {
    if (!file || !periodKey.match(/^\d{4}-\d{2}$/)) {
      setUploadErr('Pick a zip file and a period like 2026-03.');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setUploadErr('Only .zip files are accepted.');
      return;
    }
    setUploading(true);
    setUploadErr(null);
    setUploadPct(0);
    try {
      const path = `ingest-sage/${slug}/${periodKey}/${Date.now()}_${file.name}`;
      const ref  = storageRef(storage, path);
      const meta = { customMetadata: { uploaderUid: auth.currentUser?.uid || 'unknown' } };
      const task = uploadBytesResumable(ref, file, meta);
      task.on('state_changed',
        s => setUploadPct(Math.round(s.bytesTransferred / s.totalBytes * 100)),
        e => { setUploadErr(e.message); setUploading(false); },
        () => {
          setUploading(false);
          setFile(null);
          setUploadPct(0);
          // Show "waiting for parser" until the function writes its run doc
          // (typically <10s). The onSnapshot subscription clears this once it
          // sees a run with startedAt at or after our uploadFinishedAt.
          setUploadFinishedAt(Date.now());
          setWaitingForRun(true);
        }
      );
    } catch (e) {
      setUploadErr(e.message);
      setUploading(false);
    }
  };

  // ── Status pill ────────────────────────────────────────────
  const statusPill = (status) => {
    const colors = {
      running:      { bg: 'rgba(200,149,32,0.10)', fg: 'var(--amber)' },
      success:      { bg: 'rgba(26,173,99,0.10)',  fg: 'var(--green-l)' },
      failed:       { bg: 'rgba(176,40,40,0.10)',  fg: 'var(--red)' },
      needs_review: { bg: 'rgba(200,149,32,0.14)', fg: 'var(--amber)' },
    };
    const c = colors[status] || { bg: 'rgba(100,100,120,0.08)', fg: 'var(--page-muted)' };
    const label = status === 'needs_review' ? 'needs review' : (status || 'pending');
    return (
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
        padding: '3px 8px', borderRadius: 2, background: c.bg, color: c.fg,
      }}>{label}</span>
    );
  };

  // ── Coverage cell renderer ─────────────────────────────────
  const coverageCell = (cov) => {
    if (!cov) return <span style={{color:'var(--page-muted)'}}>—</span>;
    const gl = cov.fact_gl_monthly;
    const bs = cov.fact_bs_monthly;
    const part = (label, t) => {
      if (!t) return `${label}: —`;
      const exp = cov.expected_entities;
      const ok = t.missing_entities.length === 0;
      return `${label}: ${t.loaded_entities}/${exp}${ok ? ' ✓' : ` ⚠ (${t.missing_entities.length} missing)`}`;
    };
    return (
      <div style={{display:'flex', flexDirection:'column', gap:2, fontSize:10}}>
        <span>{part('P&L', gl)}</span>
        <span>{part('BS', bs)}</span>
      </div>
    );
  };

  // ── Approve handler ────────────────────────────────────────
  const approveRun = async (runId) => {
    const note = window.prompt(
      'Approve this run? Optional note (e.g. why the missing entities are expected):',
      ''
    );
    if (note === null) return; // cancelled
    setApproving(runId);
    try {
      await adminPost('approveIngestRun', { companyId, runId, note: note || null });
    } catch (e) {
      alert(`Approval failed: ${e.message}`);
    } finally {
      setApproving(null);
    }
  };

  return (
    <div>
      {/* Upload card */}
      <div style={S.card}>
        <div style={S.cardTitle}>Upload Monthly Pack</div>
        <div style={{display:'grid', gridTemplateColumns: '160px 1fr auto', gap: 12, alignItems: 'end'}}>
          <Field label="Period (YYYY-MM)">
            <input style={S.input} value={periodKey} onChange={e => setPeriodKey(e.target.value)} placeholder="2026-03" />
          </Field>
          <Field label="Zip file">
            <input
              type="file" accept=".zip"
              onChange={e => { setFile(e.target.files[0] || null); setUploadErr(null); }}
              style={{...S.input, padding: 6}}
            />
          </Field>
          <button style={S.saveBtn} onClick={handleUpload} disabled={uploading || !file}>
            {uploading ? `Uploading ${uploadPct}%` : '⬆ Upload'}
          </button>
        </div>
        {uploading && (
          <div style={{marginTop:12, height: 3, background: 'var(--card-border)', borderRadius: 2}}>
            <div style={{height: '100%', width: `${uploadPct}%`, background: 'var(--gold)', borderRadius: 2, transition: 'width 0.2s'}} />
          </div>
        )}
        {uploadErr && <div style={{...S.errorMsg, marginTop:12}}>⚠ {uploadErr}</div>}
        <div style={{
          marginTop: 12, padding: '8px 12px', fontSize: 11, color: 'var(--page-muted)',
          background: 'rgba(200,149,32,0.05)', border: '1px solid rgba(200,149,32,0.18)', borderRadius: 2, lineHeight: 1.55,
        }}>
          Uploads to <code style={{fontFamily:'var(--font-mono)',fontSize:10.5}}>gs://&lt;bucket&gt;/ingest-sage/{slug}/{periodKey}/</code> and triggers the Sage parser. Re-uploading the same period replaces its FactGLMonthly rows (idempotent).
        </div>
      </div>

      {/* Parser status block — fills the gap between upload-complete and run-row-appears */}
      <ParserStatusBlock
        waiting={waitingForRun}
        latestRun={latestRun}
        tickKey={tick}
      />

      {/* History */}
      <div style={S.card}>
        <div style={S.cardTitle}>Ingest History (last 20)</div>
        {runsLoading ? (
          <div style={{padding:20, fontFamily:'var(--font-mono)', fontSize:11, color:'var(--page-muted)'}}>Loading…</div>
        ) : runs.length === 0 ? (
          <div style={{padding:20, fontFamily:'var(--font-mono)', fontSize:11, color:'var(--page-muted)'}}>No ingest runs yet. Upload a zip above to get started.</div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Run</th>
                <th style={S.th}>Period</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Files</th>
                <th style={S.th}>Fact Rows</th>
                <th style={S.th}>Coverage</th>
                <th style={S.th}>Anomalies</th>
                <th style={S.th}>When</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(r => {
                const s = r.summary || {};
                const start = r.startedAt?.toDate?.() || (r.startedAt ? new Date(r.startedAt) : null);
                const isExpanded = expandedRun === r.id;
                const hasGaps = (s.coverage?.fact_gl_monthly?.missing_entities?.length > 0)
                             || (s.coverage?.fact_bs_monthly?.missing_entities?.length > 0);
                const canApprove = r.status === 'needs_review';
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      style={{cursor: hasGaps ? 'pointer' : 'default'}}
                      onClick={() => hasGaps && setExpandedRun(isExpanded ? null : r.id)}
                    >
                      <td style={S.tdM}>{r.id}</td>
                      <td style={S.tdM}>{r.periodKey || '—'}</td>
                      <td style={S.td}>{statusPill(r.status)}</td>
                      <td style={S.tdM}>{(s.files_processed ?? '—')}{s.files_skipped != null ? ` (skipped ${s.files_skipped})` : ''}</td>
                      <td style={S.tdM}>{s.fact_rows ?? '—'}</td>
                      <td style={S.tdM}>{coverageCell(s.coverage)}</td>
                      <td style={S.tdM} title={(s.anomalies || []).join('\n')}>
                        {s.anomalies?.length ? `⚠ ${s.anomalies.length}` : '—'}
                      </td>
                      <td style={S.tdM}>{start ? start.toLocaleString() : '—'}</td>
                    </tr>
                    {isExpanded && hasGaps && (
                      <tr>
                        <td colSpan={8} style={{padding:'14px 18px', background:'rgba(200,149,32,0.04)', borderBottom:'1px solid var(--card-border)'}}>
                          <CoverageDetail
                            coverage={s.coverage}
                            run={r}
                            canApprove={canApprove}
                            onApprove={() => approveRun(r.id)}
                            approving={approving === r.id}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Status block shown between the upload card and the history table while
// something is in flight. Fills the visible gap that used to exist between
// "upload finished at 100%" and "the new run row appears in the table".
// eslint-disable-next-line no-unused-vars
function ParserStatusBlock({ waiting, latestRun, tickKey }) {
  const running = latestRun && latestRun.status === 'running';
  if (!waiting && !running) return null;

  let label, sub;
  if (running) {
    const start = latestRun.startedAt?.toDate?.() || (latestRun.startedAt ? new Date(latestRun.startedAt) : null);
    const elapsedSec = start ? Math.max(0, Math.floor((Date.now() - start.getTime()) / 1000)) : 0;
    label = `⚙ Parsing Sage pack — elapsed ${elapsedSec}s`;
    sub = 'Unzipping, walking files, mapping entities/GL accounts, writing facts to BigQuery, running reconciliation.';
  } else {
    label = '✓ Upload complete — waiting for parser to start…';
    sub = 'Cloud Function picks up the zip within ~5 seconds.';
  }

  return (
    <div style={{
      margin: '14px 0', padding: '12px 16px',
      background: 'rgba(200,149,32,0.07)', border: '1px solid rgba(200,149,32,0.3)', borderRadius: 2,
    }}>
      <div style={{fontFamily:'var(--font-mono)', fontSize:11, color:'var(--amber)', letterSpacing:'0.05em'}}>
        {label}
      </div>
      <div style={{fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', marginTop:4}}>
        {sub}
      </div>
    </div>
  );
}

// Coverage detail panel shown when a needs_review run is expanded.
// Lists the entities present in DimEntity but absent from the fact tables
// for the period(s) touched, then offers an "Approve anyway" button.
function CoverageDetail({ coverage, run, canApprove, onApprove, approving }) {
  if (!coverage) return null;
  const sections = [
    { label: 'Missing from P&L (FactGLMonthly)', cov: coverage.fact_gl_monthly },
    { label: 'Missing from Balance Sheet (FactBSMonthly)', cov: coverage.fact_bs_monthly },
  ].filter(s => s.cov && s.cov.missing_entities.length > 0);

  const approved = run.originalStatus === 'needs_review' && run.status === 'success';
  const s = run.summary || {};
  const bsAnomalies  = s.bs_anomalies || [];
  const bsUnmapped   = s.bs_unmapped_entities || [];
  const bsImbalances = s.bs_imbalances || [];

  return (
    <div style={{fontFamily:'var(--font-mono)', fontSize:11, lineHeight:1.6}}>
      <div style={{marginBottom:10, fontSize:10, letterSpacing:'0.1em', color:'var(--page-muted)', textTransform:'uppercase'}}>
        Reconciliation against DimEntity ({coverage.expected_entities} active entities)
      </div>
      {sections.map(({ label, cov }) => (
        <div key={label} style={{marginBottom:14}}>
          <div style={{color:'var(--amber)', marginBottom:6}}>
            ⚠ {label} — {cov.missing_entities.length} of {coverage.expected_entities}
          </div>
          <ul style={{margin:0, paddingLeft:18, color:'var(--page-text)'}}>
            {cov.missing_entities.map(e => (
              <li key={e.entity_key} style={{marginBottom:2}}>
                {e.legal_name} <span style={{color:'var(--page-muted)'}}>({e.entity_key})</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {bsAnomalies.length > 0 && (
        <div style={{marginBottom:14}}>
          <div style={{color:'var(--amber)', marginBottom:6}}>⚠ BS PDFs that failed to parse — {bsAnomalies.length}</div>
          <ul style={{margin:0, paddingLeft:18, color:'var(--page-text)'}}>
            {bsAnomalies.slice(0, 15).map((a, i) => (
              <li key={i} style={{marginBottom:2}}>
                <code style={{fontSize:10}}>{a.file}</code>
                <span style={{color:'var(--page-muted)'}}> — {a.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {bsUnmapped.length > 0 && (
        <div style={{marginBottom:14}}>
          <div style={{color:'var(--amber)', marginBottom:6}}>⚠ BS PDFs whose entity name doesn't match any DimEntity — {bsUnmapped.length}</div>
          <ul style={{margin:0, paddingLeft:18, color:'var(--page-text)'}}>
            {bsUnmapped.slice(0, 15).map((u, i) => (
              <li key={i} style={{marginBottom:2}}>
                "{u.entity_name}" <span style={{color:'var(--page-muted)'}}>from {u.file}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {bsImbalances.length > 0 && (
        <div style={{marginBottom:14}}>
          <div style={{color:'var(--amber)', marginBottom:6}}>⚠ BS balance imbalances &gt;1% — {bsImbalances.length}</div>
          <ul style={{margin:0, paddingLeft:18, color:'var(--page-text)'}}>
            {bsImbalances.slice(0, 15).map((b, i) => (
              <li key={i} style={{marginBottom:2}}>
                {b.legal_name} <span style={{color:'var(--page-muted)'}}>
                  ({b.period_key}): assets {b.assets.toLocaleString()} vs liab+equity {b.liab_equity.toLocaleString()} — off by {b.pct}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {approved && (
        <div style={{marginTop:10, padding:'8px 10px', background:'rgba(26,173,99,0.06)', border:'1px solid rgba(26,173,99,0.2)', borderRadius:2, color:'var(--green-l)'}}>
          ✓ Approved by {run.approvedByEmail || run.approvedBy}
          {run.approvalNote ? ` — "${run.approvalNote}"` : ''}
        </div>
      )}
      {canApprove && (
        <div style={{marginTop:12, display:'flex', alignItems:'center', gap:10}}>
          <button
            onClick={onApprove}
            disabled={approving}
            style={{
              fontFamily:'var(--font-mono)', fontSize:10, letterSpacing:'0.1em', textTransform:'uppercase',
              padding:'6px 14px', borderRadius:2, cursor: approving ? 'wait' : 'pointer',
              background:'var(--gold)', color:'#000', border:'none', opacity: approving ? 0.6 : 1,
            }}
          >
            {approving ? 'Approving…' : 'Approve anyway'}
          </button>
          <span style={{fontSize:10, color:'var(--page-muted)'}}>
            Confirm the missing entities are expected (e.g. closed store, marketing entity without BS).
          </span>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: BRANDS  (MultiEntityFinance only)
// Distinct brands are discovered from DimEntity; admin uploads a logo
// per brand + can rename the display label. Logos are pulled by
// ReportRender for per-brand management report covers.
// ════════════════════════════════════════════════════════════
function TabBrands({ companyId, company }) {
  const [brands, setBrands]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await adminPost('listBrands', { companyId });
      setBrands(r.brands || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const updateLocal = (slug, patch) => {
    setBrands(prev => prev.map(b => b.slug === slug ? { ...b, ...patch } : b));
  };

  return (
    <div>
      <div style={S.card}>
        <div style={S.cardTitle}>Brands ({brands.length})</div>
        {loading && <div style={{padding:14, fontFamily:'var(--font-mono)', fontSize:11, color:'var(--page-muted)'}}>Loading…</div>}
        {error && <div style={S.errorMsg}>⚠ {error}</div>}
        {!loading && brands.length === 0 && !error && (
          <div style={{padding:14, fontFamily:'var(--font-mono)', fontSize:11, color:'var(--page-muted)'}}>
            No brands found. Add entities with a brand field on the Entities tab — they'll appear here automatically.
          </div>
        )}
        {!loading && brands.length > 0 && (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Brand</th>
                <th style={S.th}>Display Name</th>
                <th style={S.th}>Logo</th>
                <th style={S.th}>Entities</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {brands.map(b => (
                <BrandRow
                  key={b.slug}
                  brand={b}
                  company={company}
                  companyId={companyId}
                  onLocalUpdate={(patch) => updateLocal(b.slug, patch)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={{fontSize:11, color:'var(--page-muted)', padding:'0 4px', lineHeight:1.6}}>
        Brand logos appear on per-brand Management Report covers (Group Consolidated → ⎙ Generate Management Reports).
        The consolidated report uses the tenant logo set on Overview.
      </div>
    </div>
  );
}

function BrandRow({ brand, company, companyId, onLocalUpdate }) {
  const [displayName, setDisplayName] = useState(brand.displayName || brand.name);
  const [logoUrl,     setLogoUrl]     = useState(brand.logoUrl);
  const [uploading,   setUploading]   = useState(false);
  const [pct,         setPct]         = useState(0);
  const [err,         setErr]         = useState(null);
  const [saving,      setSaving]      = useState(false);
  const fileRef = React.useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true); setPct(0); setErr(null);
    try {
      const ext = file.name.split('.').pop();
      const path = `logos/${company.slug}/brand_${brand.slug}_${Date.now()}.${ext}`;
      const sRef = storageRef(storage, path);
      const task = uploadBytesResumable(sRef, file);
      task.on('state_changed',
        snap => setPct(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
        e    => { setErr(e.message); setUploading(false); },
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            await adminPost('upsertBrand', { companyId, brand: { slug: brand.slug, logoUrl: url } });
            setLogoUrl(url);
            onLocalUpdate({ logoUrl: url });
          } catch (e) { setErr(e.message); }
          finally { setUploading(false); }
        }
      );
    } catch (e) { setErr(e.message); setUploading(false); }
  };

  const saveDisplayName = async () => {
    if (displayName === brand.displayName) return;
    setSaving(true); setErr(null);
    try {
      await adminPost('upsertBrand', { companyId, brand: { slug: brand.slug, displayName } });
      onLocalUpdate({ displayName });
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <tr>
      <td style={S.tdM}>{brand.name}</td>
      <td style={S.td}>
        <input
          type="text"
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          onBlur={saveDisplayName}
          style={{...S.input, padding:'4px 8px', fontSize:12, width:'100%'}}
          disabled={saving}
        />
      </td>
      <td style={S.td}>
        <div style={{display:'flex', alignItems:'center', gap:10}}>
          {logoUrl ? (
            <img src={logoUrl} alt={brand.name} style={{height:32, maxWidth:80, objectFit:'contain', background:'#fff', padding:2, borderRadius:2}} />
          ) : (
            <span style={{fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)'}}>— no logo —</span>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{display:'none'}}
            onChange={e => { handleFile(e.target.files[0]); e.target.value = ''; }}
          />
          <button
            style={{...S.saveBtn, padding:'4px 10px', fontSize:10}}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? `${pct}%` : (logoUrl ? 'Replace' : 'Upload')}
          </button>
        </div>
        {err && <div style={{fontSize:10, color:'var(--red)', marginTop:4}}>⚠ {err}</div>}
      </td>
      <td style={S.tdM}>{brand.entityCount}</td>
      <td style={S.td}>
        {logoUrl && (
          <button
            style={{...S.printBtn || S.input, padding:'4px 8px', fontSize:10, background:'transparent', border:'1px solid var(--card-border)', color:'var(--page-muted)', cursor:'pointer', borderRadius:2}}
            onClick={async () => {
              if (!window.confirm(`Remove logo for ${brand.name}?`)) return;
              try {
                await adminPost('upsertBrand', { companyId, brand: { slug: brand.slug, logoUrl: null } });
                setLogoUrl(null);
                onLocalUpdate({ logoUrl: null });
              } catch (e) { setErr(e.message); }
            }}
          >
            Clear logo
          </button>
        )}
      </td>
    </tr>
  );
}

// ════════════════════════════════════════════════════════════
// TabEntities (MultiEntityFinance only)
// ════════════════════════════════════════════════════════════
const SA_PROVINCES = [
  'Eastern Cape','Free State','Gauteng','KwaZulu-Natal','Limpopo',
  'Mpumalanga','North West','Northern Cape','Western Cape',
];
const ENTITY_TYPES = ['Store','DC','HeadOffice','Marketing','Recruitment','Property'];
const COUNTRIES    = ['ZA','BW','ZM'];

function TabEntities({ companyId, company }) {
  const [entities, setEntities] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [searchQ,  setSearchQ]  = useState('');
  const [editing,  setEditing]  = useState(null); // null | 'new' | entity object
  const [bulkUp,   setBulkUp]   = useState({ busy: false, msg: null, ok: null });

  const loadEntities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await adminPost('listEntities', { companyId });
      setEntities(r.entities || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { loadEntities(); }, [loadEntities]);

  const filtered = useMemo(() => {
    if (!searchQ) return entities;
    const q = searchQ.toLowerCase();
    return entities.filter(e =>
      (e.entity_key || '').toLowerCase().includes(q)
      || (e.legal_name || '').toLowerCase().includes(q)
      || (e.province || '').toLowerCase().includes(q)
      || (e.town || '').toLowerCase().includes(q)
      || (e.brand || '').toLowerCase().includes(q)
      || (e.entity_type || '').toLowerCase().includes(q)
    );
  }, [entities, searchQ]);

  const handleSave = async (entity) => {
    await adminPost('upsertEntity', { companyId, entity });
    setEditing(null);
    await loadEntities();
  };

  const handleBulkUpload = async (file) => {
    setBulkUp({ busy: true, msg: null, ok: null });
    try {
      const rows = await parseExcel(file);
      const branches = rows.map(r => ({
        code:       String(r.EntityCode || r.BranchCode || r.code || '').toUpperCase(),
        name:       r.LegalName  || r.BranchName  || r.name || '',
        type:       r.EntityType || r.Type || r.type || 'Store',
        entityType: r.EntityType || r.Type || 'Store',
        brand:      r.Brand      || r.brand || '',
        country:    r.Country    || 'ZA',
        province:   r.Province   || r.Region || '',
        region:     r.Region     || r.Province || '',
        town:       r.Town       || r.City || r.town || r.city || '',
        currency:   r.Currency   || 'ZAR',
        sageCoName: r.SageCoName || '',
        active:     (r.Active || 'Yes').toString().toLowerCase() !== 'no',
      })).filter(b => b.code && b.name);
      if (!branches.length) throw new Error('No valid rows found (need EntityCode + LegalName).');
      const r2 = await adminPost('reloadEntities', { companyId, branches });
      setBulkUp({ busy: false, msg: `✓ Reloaded ${r2.count} entities`, ok: true });
      await loadEntities();
    } catch (e) {
      setBulkUp({ busy: false, msg: `⚠ ${e.message}`, ok: false });
    }
  };

  const handleDownload = () => {
    const cols = ['EntityCode','LegalName','Brand','EntityType','Country','Province','Town','Currency','SageCoName','Active'];
    const data = entities.map(e => ({
      EntityCode: e.entity_key,
      LegalName:  e.legal_name,
      Brand:      e.brand,
      EntityType: e.entity_type,
      Country:    e.country || '',
      Province:   e.province || '',
      Town:       e.town || '',
      Currency:   e.currency || '',
      SageCoName: e.sage_co_name || '',
      Active:     e.is_active ? 'Yes' : 'No',
    }));
    const ws = XLSX.utils.json_to_sheet(data, { header: cols });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Entities');
    XLSX.writeFile(wb, `${company.slug || 'entities'}_entities.xlsx`);
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display:'flex', gap:8, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
        <input
          placeholder="Search code / name / province / brand…"
          value={searchQ} onChange={e=>setSearchQ(e.target.value)}
          style={{ ...S.input, flex:'0 0 280px' }}
        />
        <div style={{ flex:1 }} />
        <button style={S.saveBtn} onClick={()=>setEditing('new')}>+ Add Entity</button>
        <label style={{ ...S.ghostBtn, display:'inline-block', cursor:'pointer' }}>
          {bulkUp.busy ? 'Uploading…' : '⬆ Re-upload Excel'}
          <input
            type="file" accept=".xlsx,.xls" style={{ display:'none' }}
            onChange={e => { const f = e.target.files[0]; e.target.value=''; if (f) handleBulkUpload(f); }}
            disabled={bulkUp.busy}
          />
        </label>
        <button style={S.ghostBtn} onClick={handleDownload}>⬇ Download Current</button>
      </div>

      {bulkUp.msg && (
        <div style={{ ...(bulkUp.ok ? S.successMsg : S.errorMsg), marginBottom:12 }}>
          {bulkUp.msg}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ padding:20, fontFamily:'var(--font-mono)', fontSize:11, color:'var(--page-muted)' }}>Loading…</div>
      ) : error ? (
        <div style={S.errorMsg}>⚠ {error}</div>
      ) : (
        <div style={{ overflow:'auto', border:'1px solid var(--card-border)', borderRadius:2 }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Entity Key</th>
                <th style={S.th}>Legal Name</th>
                <th style={S.th}>Brand</th>
                <th style={S.th}>Type</th>
                <th style={S.th}>Country</th>
                <th style={S.th}>Province</th>
                <th style={S.th}>Town</th>
                <th style={S.th}>Active</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.entity_key}>
                  <td style={S.td}>
                    <code style={{ fontFamily:'var(--font-mono)', fontSize:11 }}>{e.entity_key}</code>
                  </td>
                  <td style={S.td}>{e.legal_name}</td>
                  <td style={S.td}>{e.brand}</td>
                  <td style={S.td}>{e.entity_type}</td>
                  <td style={S.td}>{e.country || ''}</td>
                  <td style={S.td}>
                    {e.province || <span style={{ color:'var(--page-muted)' }}>—</span>}
                  </td>
                  <td style={S.td}>
                    {e.town || <span style={{ color:'var(--page-muted)' }}>—</span>}
                  </td>
                  <td style={S.td}>{e.is_active ? '✓' : '✗'}</td>
                  <td style={S.td}>
                    <button style={{ ...S.ghostBtn, padding:'5px 10px', fontSize:9 }} onClick={()=>setEditing(e)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div style={{ padding:24, textAlign:'center', color:'var(--page-muted)', fontSize:12 }}>
              No entities match your search.
            </div>
          )}
        </div>
      )}
      <div style={{ marginTop:8, fontSize:11, color:'var(--page-muted)' }}>
        {filtered.length} of {entities.length} entities
        {' · '}
        <span style={{ color:'var(--page-muted)' }}>
          {entities.filter(e=>!e.province && e.country==='ZA' && e.is_active).length} ZA-active without province
        </span>
      </div>

      {editing && (
        <EntityEditModal
          entity={editing === 'new' ? null : editing}
          existingCodes={entities.map(e => (e.entity_key || '').toUpperCase())}
          onClose={()=>setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function EntityEditModal({ entity, existingCodes, onClose, onSave }) {
  const isNew = !entity;
  const [form, setForm] = useState({
    entity_key:   entity?.entity_key || '',
    legal_name:   entity?.legal_name || '',
    brand:        entity?.brand || 'Pedros',
    entity_type:  entity?.entity_type || 'Store',
    country:      entity?.country || 'ZA',
    province:     entity?.province || '',
    town:         entity?.town || '',
    currency:     entity?.currency || 'ZAR',
    sage_co_name: entity?.sage_co_name || '',
    is_active:    entity?.is_active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState(null);

  const submit = async () => {
    setErr(null);
    if (!form.entity_key || !form.legal_name) {
      setErr('Entity Code and Legal Name are required'); return;
    }
    if (isNew && existingCodes.includes(form.entity_key.toUpperCase())) {
      setErr('Entity Code already exists. Use Edit on that row instead.'); return;
    }
    setSaving(true);
    try {
      await onSave({ ...form, entity_key: form.entity_key.toUpperCase() });
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const backdrop = {
    position:'fixed', inset:0, background:'rgba(0,0,0,0.6)',
    display:'flex', alignItems:'center', justifyContent:'center',
    zIndex:1000, padding:20,
  };
  const card = {
    background:'var(--card)', border:'1px solid var(--card-border)',
    borderRadius:4, padding:24, width:'100%', maxWidth:640, maxHeight:'90vh',
    overflow:'auto',
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={card} onClick={e=>e.stopPropagation()}>
        <div style={S.cardTitle}>{isNew ? 'Add Entity' : `Edit ${entity.entity_key}`}</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <Field label="Entity Code *">
            <input
              style={S.input} value={form.entity_key}
              disabled={!isNew}
              onChange={e=>setForm(f=>({ ...f, entity_key: e.target.value }))}
              placeholder="e.g. PEDROS-RUSTENBERG"
            />
          </Field>
          <Field label="Brand">
            <select style={S.input} value={form.brand}
                    onChange={e=>setForm(f=>({ ...f, brand: e.target.value }))}>
              <option>Pedros</option>
              <option>BirdAndCo</option>
              <option>Wackys</option>
            </select>
          </Field>
          <Field label="Legal Name *">
            <input
              style={S.input} value={form.legal_name}
              onChange={e=>setForm(f=>({ ...f, legal_name: e.target.value }))}
            />
          </Field>
          <Field label="Type">
            <select style={S.input} value={form.entity_type}
                    onChange={e=>setForm(f=>({ ...f, entity_type: e.target.value }))}>
              {ENTITY_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Country">
            <select style={S.input} value={form.country}
                    onChange={e=>setForm(f=>({ ...f, country: e.target.value }))}>
              {COUNTRIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Province">
            <select style={S.input} value={form.province}
                    onChange={e=>setForm(f=>({ ...f, province: e.target.value }))}
                    disabled={form.country !== 'ZA'}>
              <option value="">— none —</option>
              {SA_PROVINCES.map(p => <option key={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Town / City">
            <input
              style={S.input} value={form.town}
              onChange={e=>setForm(f=>({ ...f, town: e.target.value }))}
              placeholder="e.g. Sandton"
            />
          </Field>
          <Field label="Currency">
            <input
              style={S.input} value={form.currency}
              onChange={e=>setForm(f=>({ ...f, currency: e.target.value }))}
            />
          </Field>
          <Field label="Sage CoName (Sage Evolution join key)">
            <input
              style={S.input} value={form.sage_co_name}
              onChange={e=>setForm(f=>({ ...f, sage_co_name: e.target.value }))}
              placeholder="Must match CoName column in Sage files"
            />
          </Field>
          <Field label="Active">
            <label style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 12px' }}>
              <input
                type="checkbox" checked={form.is_active}
                onChange={e=>setForm(f=>({ ...f, is_active: e.target.checked }))}
              />
              <span style={{ fontSize:12 }}>{form.is_active ? 'Active' : 'Closed / inactive'}</span>
            </label>
          </Field>
        </div>
        {err && <div style={{ ...S.errorMsg, marginTop:12 }}>⚠ {err}</div>}
        <div style={{ display:'flex', gap:8, marginTop:18, justifyContent:'flex-end' }}>
          <button style={S.ghostBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={S.saveBtn} onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : (isNew ? 'Add Entity' : 'Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
export default function AdminClientDetail() {
  const { companyId } = useParams();
  const navigate      = useNavigate();
  const [company,  setCompany]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('overview');

  const load = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, 'companies', companyId));
      if (snap.exists()) setCompany({ id: snap.id, ...snap.data() });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 48, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' }}>Loading...</div>;
  if (!company) return <div style={{ padding: 48, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--red)' }}>Client not found.</div>;

  return (
    <div style={S.page}>
      <div style={S.back} onClick={() => navigate('/admin/clients')}>← Back to Client Management</div>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={S.title}>{company.name}</div>
          <div style={S.sub}>{company.slug} · {company.industry || 'No industry'} · {company.plan}</div>
        </div>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
          padding: '5px 12px', borderRadius: 2,
          background: company.status === 'active' ? 'rgba(26,173,99,0.1)' : 'rgba(196,122,26,0.1)',
          color: company.status === 'active' ? 'var(--green-l)' : 'var(--amber)',
        }}>{company.status}</span>
      </div>

      <div style={S.tabs}>
        {[
          ['overview', 'Overview'],
          ['users', 'Users'],
          ['groups', 'Groups'],
          ...(company.solution === 'MultiEntityFinance'
            ? [['entities', 'Entities'], ['brands', 'Brands'], ['monthlyPacks', 'Monthly Packs']]
            : []),
          ['audit', 'Audit Log'],
          ['settings', 'Settings'],
        ].map(([key, label]) => (
          <button key={key} style={S.tab(tab === key)} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>

      {tab === 'overview'     && <TabOverview     company={company} companyId={companyId} onRefresh={load} />}
      {tab === 'users'        && <TabUsers        companyId={companyId} company={company} />}
      {tab === 'groups'       && <TabGroups       companyId={companyId} company={company} />}
      {tab === 'entities'     && <TabEntities     companyId={companyId} company={company} />}
      {tab === 'brands'       && <TabBrands       companyId={companyId} company={company} />}
      {tab === 'monthlyPacks' && <TabMonthlyPacks companyId={companyId} company={company} />}
      {tab === 'audit'        && <TabAudit        companyId={companyId} />}
      {tab === 'settings'     && <TabSettings     company={company} companyId={companyId} onRefresh={load} navigate={navigate} />}
    </div>
  );
}
